import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { loadStripe } from '@stripe/stripe-js'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import { personalizeTitle } from '../lib/formatters'
import { flattenErrorCount } from '../lib/validators'
import { apiPost, apiGet } from '../lib/api'
import StripePaymentModal from '../components/form/StripePaymentModal'
import PaymentMethodPicker from '../components/form/PaymentMethodPicker'
import { isValidationDisabled } from '../lib/validationMode'
import StepLayout from '../components/form/StepLayout'
import Band from '../components/form/Band'
import ErrorBanner from '../components/form/ErrorBanner'
import { useStepErrorSummary } from '../hooks/useStepErrorSummary'

const STEP_ID = 'networkplus'
const SETUP_AMOUNT_CENTS = 499700

let _stripePromise = null
function getStripe() {
  if (_stripePromise) return _stripePromise
  const PK =
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_BRITE ||
    import.meta.env.VITE_STRIPE_PUBLIC_KEY
  if (!PK) return Promise.resolve(null)
  _stripePromise = loadStripe(PK)
  return _stripePromise
}

const strictSchema = z.object({
  isMemberPlusMember: z.boolean({ message: 'Choose Yes or Skip' }),
})
const looseSchema = z.object({
  isMemberPlusMember: z.any(),
}).passthrough()
const schema = isValidationDisabled() ? looseSchema : strictSchema

function ChoiceCard({ selected, onClick, heading, icon, body, recommended }) {
  return (
    <button
      type="button"
      className={`np-choice${selected ? ' is-selected' : ''}`}
      onClick={onClick}
      aria-pressed={selected}
    >
      {recommended && (
        <span className="np-choice-recommended-chip">RECOMMENDED</span>
      )}
      <div className="np-choice-heading">
        <span className="np-choice-icon" aria-hidden="true">
          {icon}
        </span>
        {heading}
      </div>
      <div className="np-choice-body">{body}</div>
    </button>
  )
}

function formatDollars(cents) {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

const SS_SESSION = 'brite-onboarding-session-id'
function getSessionId() {
  if (typeof sessionStorage === 'undefined') return undefined
  return sessionStorage.getItem(SS_SESSION) || undefined
}

function formatBrand(brand) {
  if (!brand) return 'Card'
  return brand.charAt(0).toUpperCase() + brand.slice(1)
}

function formatExpiry(expMonth, expYear) {
  const mm = String(expMonth || '').padStart(2, '0')
  const yy = String(expYear || '').slice(-2)
  return `${mm}/${yy}`
}

export default function Step5NetworkPlus() {
  const step = STEPS.find((s) => s.id === 'networkplus')
  const practiceName = useOnboardingStore(
    (s) => s.formData?.details?.practiceName,
  )
  const persisted = useOnboardingStore((s) => s.formData[STEP_ID])
  const updateFormData = useOnboardingStore((s) => s.updateFormData)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const { setHandler, clearHandler, reportErrorCount, pulse, focusFirstError } =
    useStepSubmit()

  const paymentStatus = persisted?.paymentStatus
  const stripeCustomerId = useOnboardingStore(
    (s) => s.formData?.stripe?.stripeCustomerId,
  )
  const achAccount = useOnboardingStore(
    (s) => s.formData?.managedBilling?.achAccount,
  )
  const appliedPromotionCode = useOnboardingStore(
    (s) => s.formData?.stripe?.networkPlus?.appliedPromotionCode,
  )
  const setNetworkPlusPayment = useOnboardingStore(
    (s) => s.setNetworkPlusPayment,
  )
  const setAppliedPromotionCode = useOnboardingStore(
    (s) => s.setNetworkPlusAppliedPromotionCode,
  )
  const [payModal, setPayModal] = useState(null)
  const [payBusy, setPayBusy] = useState(false)
  const [payError, setPayError] = useState('')
  const [payNotice, setPayNotice] = useState('')
  const [methodPickerOpen, setMethodPickerOpen] = useState(false)
  const [pendingMethod, setPendingMethod] = useState(null)

  // CR-12: $1 card-verify state for the "Skip N+" path. The verification UI
  // moved from Step 4 (Owners) to here — only renders when user picked Skip
  // and doesn't already have a card on file.
  const stripeSaved = useOnboardingStore((s) => s.formData?.stripe)
  const hasSavedCard = Boolean(
    stripeSaved?.paymentMethodId && stripeSaved?.card?.last4,
  )
  const [verifyModal, setVerifyModal] = useState(null)
  const [verifyBusy, setVerifyBusy] = useState(false)
  const [verifyError, setVerifyError] = useState('')

  // Coupon UI state
  const [couponOpen, setCouponOpen] = useState(!!appliedPromotionCode)
  const [couponCode, setCouponCode] = useState(appliedPromotionCode?.code || '')
  const [couponBusy, setCouponBusy] = useState(false)
  const [couponError, setCouponError] = useState('')

  // Personalization-driven auto-apply: if the staff-side Personalization tab
  // attached a couponCode to this slug, apply it once on first reach of Step 5.
  const personalization = useOnboardingStore((s) => s.personalization)
  const personalizationCoupon = personalization?.couponCode || null
  const [autoApplied, setAutoApplied] = useState(false)
  const autoAppliedRef = useRef(false)

  const defaultValues = useMemo(
    () => ({
      isMemberPlusMember: persisted?.isMemberPlusMember ?? undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  const selected = useWatch({ control, name: 'isMemberPlusMember' })

  useEffect(() => {
    if (selected === true) {
      // Persist the choice immediately so Step 4's pay-card gate sees it.
      updateFormData(STEP_ID, { isMemberPlusMember: true })
      updateFormData('providers', { providers: [] })
    } else if (selected === false) {
      const current =
        useOnboardingStore.getState().formData?.providers?.providers
      if (!current || current.length === 0) {
        updateFormData('providers', {
          providers: [{ fullName: '', email: '', locationIds: [] }],
        })
      }
      updateFormData(STEP_ID, {
        isMemberPlusMember: false,
        paymentStatus: undefined,
        paymentIntentId: undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  const onValid = (data) => {
    const fresh = useOnboardingStore.getState().formData
    const freshPaymentStatus = fresh?.networkplus?.paymentStatus
    const freshHasCard = Boolean(
      fresh?.stripe?.paymentMethodId && fresh?.stripe?.card?.last4,
    )
    // Debug mode (?debug=1) skips the payment/card gates so testers can advance
    // without a real charge or card. Never bypassed in production.
    const bypass = isValidationDisabled()
    if (!bypass && data.isMemberPlusMember === true && freshPaymentStatus !== 'paid') {
      setPayError('Complete payment to continue.')
      pulse()
      return
    }
    if (!bypass && data.isMemberPlusMember === false && !freshHasCard) {
      setVerifyError('Add a card on file to continue.')
      pulse()
      return
    }
    updateFormData(STEP_ID, { isMemberPlusMember: data.isMemberPlusMember })
    nextStep()
  }
  const { submitAttempted, errorMessages, onInvalid } = useStepErrorSummary(
    STEP_ID,
    errors,
  )

  const applyCoupon = async () => {
    const trimmed = couponCode.trim().toUpperCase()
    if (!trimmed) {
      setCouponError('Enter a code.')
      return
    }
    setCouponBusy(true)
    setCouponError('')
    try {
      const res = await apiGet('/coupon', { code: trimmed })
      if (!res.ok || !res.data?.valid) {
        setCouponError(res.data?.reason || 'Invalid code')
        if (setAppliedPromotionCode) setAppliedPromotionCode(null)
        return
      }
      const promo = {
        id: res.data.promotionCodeId,
        code: res.data.code,
        label: res.data.label,
        discountAmount: res.data.discountAmount,
        finalAmount: res.data.finalAmount,
      }
      if (setAppliedPromotionCode) setAppliedPromotionCode(promo)
      setCouponCode(res.data.code)
    } catch (err) {
      setCouponError('Could not check code. Try again.')
    } finally {
      setCouponBusy(false)
    }
  }

  const removeCoupon = () => {
    if (setAppliedPromotionCode) setAppliedPromotionCode(null)
    setCouponCode('')
    setCouponError('')
    setAutoApplied(false)
    autoAppliedRef.current = false
  }

  // Auto-apply personalization coupon once per session. Runs only when:
  //   - personalization payload provided a couponCode
  //   - nothing currently applied
  //   - we haven't already attempted on this mount
  // The ref guards against double-fire across remounts within the same
  // session (e.g. user navigates back to Step 5).
  useEffect(() => {
    if (autoAppliedRef.current) return
    if (!personalizationCoupon) return
    if (appliedPromotionCode) return
    autoAppliedRef.current = true
    setCouponCode(personalizationCoupon)
    ;(async () => {
      const trimmed = String(personalizationCoupon).trim().toUpperCase()
      if (!trimmed) return
      setCouponBusy(true)
      try {
        const res = await apiGet('/coupon', { code: trimmed })
        if (!res.ok || !res.data?.valid) {
          // Personalization referenced a coupon that no longer exists. Fail
          // silent — the manual entry stays available below.
          setCouponCode('')
          autoAppliedRef.current = false
          return
        }
        const promo = {
          id: res.data.promotionCodeId,
          code: res.data.code,
          label: res.data.label,
          discountAmount: res.data.discountAmount,
          finalAmount: res.data.finalAmount,
        }
        if (setAppliedPromotionCode) setAppliedPromotionCode(promo)
        setCouponCode(res.data.code)
        setAutoApplied(true)
      } catch {
        autoAppliedRef.current = false
      } finally {
        setCouponBusy(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalizationCoupon, appliedPromotionCode])

  const openPaymentPicker = () => {
    if (payBusy) return
    setPayError('')
    setPayNotice('')
    setMethodPickerOpen(true)
  }

  // One-click pay with the bank already connected on Step 3 (Managed Billing).
  // Backend creates the invoice + tries to pay with a PM derived from the
  // saved Financial Connections account. If the bank requires a fresh ACH
  // mandate, backend returns requires_action + clientSecret and we confirm
  // here via Stripe.js (mandate UI is brief).
  const payWithSavedBank = async () => {
    if (payBusy) return
    if (!achAccount?.accountId || !stripeCustomerId) {
      setPayError('Saved bank not available — pick another method.')
      return
    }
    setPayBusy(true)
    setPayError('')
    setPayNotice('')
    try {
      const normalizedPracticeName =
        typeof practiceName === 'string' && practiceName.trim()
          ? practiceName.trim()
          : undefined
      const res = await apiPost('/np/pay-with-fc-account', {
        customerId: stripeCustomerId,
        financialConnectionsAccountId: achAccount.accountId,
        promotionCodeId: appliedPromotionCode?.id || undefined,
        practiceName: normalizedPracticeName,
      })
      if (!res.ok || !res.data?.ok) {
        setPayError(
          res.data?.error ||
            res.data?.reason ||
            "Couldn't pay with this bank — try another method.",
        )
        return
      }
      const { paid, requires_action, clientSecret, invoiceId, paymentIntentId, waived } = res.data
      if (paid || waived) {
        updateFormData(STEP_ID, {
          paymentStatus: 'paid',
          paymentIntentId: paymentIntentId || null,
        })
        setNetworkPlusPayment({
          invoiceId: invoiceId || null,
          paymentIntentId: paymentIntentId || null,
        })
        return
      }
      if (requires_action && clientSecret) {
        const stripe = await getStripe()
        if (!stripe) {
          setPayError('Stripe failed to load — try another method.')
          return
        }
        const conf = await stripe.confirmUsBankAccountPayment(clientSecret)
        if (conf.error) {
          setPayError(conf.error.message || 'Bank payment was not authorized.')
          return
        }
        updateFormData(STEP_ID, {
          paymentStatus: 'paid',
          paymentIntentId: paymentIntentId || conf.paymentIntent?.id || null,
        })
        setNetworkPlusPayment({
          invoiceId: invoiceId || null,
          paymentIntentId: paymentIntentId || conf.paymentIntent?.id || null,
        })
        return
      }
      setPayError('Payment did not complete. Try another method.')
    } catch (err) {
      setPayError(err?.message || 'Payment failed.')
    } finally {
      setPayBusy(false)
    }
  }

  const startPaymentWithMethod = async (method) => {
    if (payBusy) return
    setPayBusy(true)
    setPayError('')
    setPendingMethod(method)
    const normalizedPracticeName =
      typeof practiceName === 'string' && practiceName.trim()
        ? practiceName.trim()
        : undefined
    const fd = useOnboardingStore.getState().formData
    const ownerEmail = fd?.owners?.owners?.[0]?.email
    const phone = fd?.details?.phone
    const locations = fd?.locations?.locations || []
    const primaryLocation =
      locations.find((l) => l?.isPrimary) || locations[0] || null
    const res = await apiPost('/payment-intent-network-plus', {
      customerId: stripeCustomerId || undefined,
      promotionCodeId: appliedPromotionCode?.id || undefined,
      practiceName: normalizedPracticeName,
      ownerEmail: ownerEmail || undefined,
      phone: phone || undefined,
      addressLine1: primaryLocation?.addressLine1 || undefined,
      addressLine2: primaryLocation?.addressLine2 || undefined,
      city: primaryLocation?.city || undefined,
      state: primaryLocation?.state || undefined,
      zip: primaryLocation?.zip || undefined,
      country: primaryLocation?.country || undefined,
      method,
    })
    setPayBusy(false)
    if (res.ok && (res.data?.waived === true || res.data?.alreadyPaid === true)) {
      updateFormData(STEP_ID, {
        paymentStatus: 'paid',
        paymentIntentId: res.data.paymentIntentId || null,
      })
      setNetworkPlusPayment({
        invoiceId: res.data.invoiceId || null,
        paymentIntentId: res.data.paymentIntentId || null,
      })
      if (res.data?.alreadyPaid === true) {
        setPayNotice(
          'Setup fee already paid for this email — continuing without re-charging.',
        )
      }
      return
    }
    if (!res.ok || !res.data?.clientSecret) {
      setPayError(
        res.error === 'timeout'
          ? 'Payment service timed out — try again.'
          : "Couldn't start payment. Try again or contact support@brite.live.",
      )
      return
    }
    setPayModal({
      clientSecret: res.data.clientSecret,
      finalAmountCents: res.data.amountDue ?? SETUP_AMOUNT_CENTS,
      invoiceId: res.data.invoiceId || null,
      paymentIntentId: res.data.paymentIntentId || null,
    })
  }

  const handlePaySuccess = (paymentIntent) => {
    const piId = paymentIntent?.id || payModal?.paymentIntentId || null
    updateFormData(STEP_ID, {
      paymentStatus: 'paid',
      paymentIntentId: piId,
    })
    if (payModal?.invoiceId || piId) {
      setNetworkPlusPayment({
        invoiceId: payModal?.invoiceId || null,
        paymentIntentId: piId,
      })
    }
    // CR-12: when setup fee was paid by card, the backend already set
    // setup_future_usage=off_session so the PM is attached to the customer.
    // Capture brand/last4 via /save-payment-method so Step 4's card-on-file
    // pill renders. Skip for us_bank_account — bank isn't reusable for
    // patient billing.
    if (pendingMethod === 'card' && piId) {
      apiPost('/save-payment-method', {
        paymentIntentId: piId,
        sessionId: getSessionId(),
      })
        .then((res) => {
          if (res.ok && res.data?.paymentMethodId) {
            updateFormData('stripe', {
              paymentMethodId: res.data.paymentMethodId,
              stripeCustomerId: res.data.customerId,
              card: res.data.card || null,
            })
          }
        })
        .catch(() => {})
    }
  }

  // CR-12: $1 verify path for users who Skip N+. Same flow as Step 4 used
  // to run, just relocated here.
  const beginCardVerification = async () => {
    if (verifyBusy) return
    const fd = useOnboardingStore.getState().formData
    const firstOwner = fd?.owners?.owners?.[0] || {}
    const firstName = String(firstOwner.fullName || '').trim()
    const firstEmail = String(firstOwner.email || '').trim()
    if (!firstName || !firstEmail) {
      setVerifyError("Fill in Owner 1's name and email first.")
      return
    }
    setVerifyBusy(true)
    setVerifyError('')
    const res = await apiPost('/payment-intent', {
      email: firstEmail,
      name: firstName,
      sessionId: getSessionId(),
    })
    setVerifyBusy(false)
    if (!res.ok || !res.data?.clientSecret) {
      setVerifyError(
        res.error === 'timeout'
          ? 'Payment service timed out — try again.'
          : "Couldn't start card verification. Try again or contact support@brite.live.",
      )
      return
    }
    setVerifyModal({ clientSecret: res.data.clientSecret, finalAmountCents: 100 })
  }

  const handleVerifySuccess = async (paymentIntent) => {
    try {
      const res = await apiPost('/save-payment-method', {
        paymentIntentId: paymentIntent.id,
        sessionId: getSessionId(),
      })
      if (!res.ok || !res.data?.paymentMethodId) {
        setVerifyError(
          'Card charged but could not be saved: ' +
            (res.data?.error || res.error || 'unknown error'),
        )
        return
      }
      updateFormData('stripe', {
        paymentMethodId: res.data.paymentMethodId,
        stripeCustomerId: res.data.customerId,
        card: res.data.card || null,
      })
    } catch (err) {
      setVerifyError('Save failed: ' + (err.message || 'unknown'))
    }
  }

  const removeSavedCard = () => {
    updateFormData('stripe', {
      paymentMethodId: null,
      stripeCustomerId: null,
      card: null,
    })
  }

  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    const status = url.searchParams.get('redirect_status')
    const piId = url.searchParams.get('payment_intent')
    if (status === 'succeeded' && piId) {
      updateFormData(STEP_ID, {
        paymentStatus: 'paid',
        paymentIntentId: piId,
      })
      url.searchParams.delete('redirect_status')
      url.searchParams.delete('payment_intent')
      url.searchParams.delete('payment_intent_client_secret')
      window.history.replaceState({}, '', url.pathname + url.search)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const submit = handleSubmit(onValid, onInvalid)
    const token = setHandler(() => submit())
    return () => clearHandler(token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, setHandler])

  useEffect(() => {
    reportErrorCount(flattenErrorCount(errors))
  }, [errors, reportErrorCount])

  useEffect(() => {
    return () => reportErrorCount(0)
  }, [reportErrorCount])

  useEffect(() => {
    if (persisted && Object.keys(persisted).length > 0) trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const choose = (value) =>
    setValue('isMemberPlusMember', value, {
      shouldDirty: true,
      shouldValidate: true,
    })

  // Lite mode: force N+ on once if undefined. The auto-applied Kashif100 coupon
  // zeroes the setup fee, so payment auto-marks as paid.
  const liteForcedRef = useRef(false)
  useEffect(() => {
    if (!liteMode) return
    if (liteForcedRef.current) return
    if (selected !== undefined) {
      liteForcedRef.current = true
      return
    }
    liteForcedRef.current = true
    choose(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liteMode, selected])

  const hasCoupon = !!appliedPromotionCode
  const finalCents = hasCoupon
    ? appliedPromotionCode.finalAmount
    : SETUP_AMOUNT_CENTS

  // 100% waiver — when the applied coupon zeroes the setup fee, there's
  // nothing to charge. Mark the step paid once so Continue advances. Only
  // fires when the user chose "Yes, enable N+" — Skip-path users still
  // need their $1 verify, no auto-mark.
  useEffect(() => {
    if (
      finalCents <= 0 &&
      paymentStatus !== 'paid' &&
      selected === true
    ) {
      updateFormData(STEP_ID, {
        paymentStatus: 'paid',
        paymentIntentId: null,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalCents, paymentStatus, selected])

  return (
    <form className="step-body step-wide" onSubmit={handleSubmit(onValid, onInvalid)} noValidate>
      <StepLayout
        question={
          liteMode
            ? `Network+ is included${
                practiceName ? ` for ${practiceName}` : ' for your practice'
              }`
            : personalizeTitle('Will {practice} offer Network+ Service?', {
                practiceName,
              })
        }
        helper={
          liteMode
            ? "You're set up to use our licensed providers in all 50 states. Toggle them on per patient when you need coverage, off when you don't."
            : "Use our licensed providers in all 50 states. Toggle them on per patient when you need coverage, off when you don't."
        }
        className="is-wide"
      >
      <Band label="Network+ Service" description="Multi-state provider coverage">
      <ErrorBanner show={submitAttempted} messages={errorMessages} />

      {autoApplied && hasCoupon && (
        <div className="np-perso-banner" role="status">
          <svg
            className="np-perso-banner-icon"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2.5 7l3 3 6-6"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="np-perso-banner-text">
            {finalCents <= 0
              ? 'Setup fee waived for your account.'
              : `Your setup fee is ${formatDollars(finalCents)}.`}
          </span>
        </div>
      )}

      <div className="np-grid">
        <div className="np-grid-left">
          {liteMode ? (
            <div className="np-lite-confirm-banner" role="status">
              <span
                className="np-lite-confirm-banner-icon"
                aria-hidden="true"
              >
                ✓
              </span>
              <span className="np-lite-confirm-banner-text">
                Already enabled — nothing to do here. Hit Continue when ready.
              </span>
            </div>
          ) : (
            <Controller
              name="isMemberPlusMember"
              control={control}
              render={() => (
                <div className="np-choice-row">
                  <ChoiceCard
                    selected={selected === true}
                    onClick={() => choose(true)}
                    icon="✓"
                    heading="Yes, enable it"
                    body="Use our provider network."
                    recommended
                  />
                  <ChoiceCard
                    selected={selected === false}
                    onClick={() => choose(false)}
                    icon="→"
                    heading="Skip for now"
                    body="Single-state only."
                  />
                </div>
              )}
            />
          )}
          {errors.isMemberPlusMember && (
            <div className="field-error" style={{ marginTop: 8 }}>
              {errors.isMemberPlusMember.message}
            </div>
          )}

          {selected === false && (
            <>
              <p className="info-block-note" style={{ marginTop: 20 }}>
                You can enable Network+ later by reaching out to support@brite.live.
              </p>

              {/* CR-12: relocated $1 card-verify pay-card box for Skip path.
                  Step 4 now also renders this box, so when the user already
                  has a saved card we hide the duplicate here — the "saved"
                  state is shown on Step 4 and the Step 5 right-column N+
                  card already covers the N+ payment story. */}
              {!hasSavedCard && (
                <div className="step-section" style={{ marginTop: 24 }}>
                  <div className="pay-card">
                    <div className="pay-card-heading">
                      <svg
                        className="pay-card-lock"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0110 0v4" />
                      </svg>
                      Secure card on file
                    </div>
                    <p className="pay-card-desc">
                      Verify with a $1 hold. Your card stays on file for patient billing.
                    </p>
                    <div className="pay-card-brands" aria-hidden="true">
                      <img src="/visa.svg" alt="" className="pay-card-brand-icon" />
                      <img src="/mastercard.svg" alt="" className="pay-card-brand-icon" />
                      <img src="/amex.svg" alt="" className="pay-card-brand-icon" />
                      <img src="/discover.svg" alt="" className="pay-card-brand-icon" />
                    </div>
                    {hasSavedCard ? (
                      <div className="pay-card-saved">
                        <span className="coupon-applied-pill">
                          {formatBrand(stripeSaved.card.brand)} ••••{' '}
                          {stripeSaved.card.last4} · exp{' '}
                          {formatExpiry(
                            stripeSaved.card.expMonth,
                            stripeSaved.card.expYear,
                          )}
                        </span>
                        <button
                          type="button"
                          className="coupon-remove"
                          onClick={removeSavedCard}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="pay-card-cta"
                        onClick={beginCardVerification}
                        disabled={verifyBusy}
                      >
                        {verifyBusy ? 'Starting…' : 'Add card for verification'}
                      </button>
                    )}
                    <div className="pay-card-footer">
                      <div className="pay-card-footer-left">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <rect x="3" y="11" width="18" height="11" rx="2" />
                          <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                        PCI DSS COMPLIANT
                      </div>
                      <div className="pay-card-footer-right">
                        <span>Powered by</span>
                        <img
                          src="/stripe-logo.png"
                          alt="Stripe"
                          className="pay-card-stripe"
                        />
                      </div>
                    </div>
                  </div>
                  {verifyError && (
                    <div className="field-error" style={{ marginTop: 8 }}>
                      {verifyError}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {selected === true && (
            <>
              <div className="step-section" style={{ marginBottom: 'var(--section-gap-y)' }}>
                <div className="attribution-card">
                  <div className="attribution-card-line">
                    Hiring your first provider is a dog chasing its own tail. Don't pay{' '}
                    <span style={{ color: 'var(--text-1)', fontWeight: 500 }}>
                      $160k/year
                    </span>{' '}
                    before you have patients to support them.
                  </div>
                  <div className="attribution-card-line-sub">
                    Network+ solves the chicken-and-egg problem.
                  </div>
                </div>
              </div>

              <div className="step-section" style={{ marginBottom: 'var(--section-gap-y)' }}>
                <div className="np-eyebrow">HOW IT WORKS</div>
                <div className="np-steps-row">
                  <div className="np-step-tile">
                    <div className="np-step-num">1</div>
                    <div className="np-step-title">Patient signs up</div>
                    <div className="np-step-body">
                      Decide per patient whether to use our network.
                    </div>
                  </div>
                  <div className="np-step-tile">
                    <div className="np-step-num">2</div>
                    <div className="np-step-title">Toggle ON</div>
                    <div className="np-step-body">
                      Our licensed providers take over their care — across all 50 states.
                    </div>
                  </div>
                  <div className="np-step-tile">
                    <div className="np-step-num">3</div>
                    <div className="np-step-title">Toggle OFF</div>
                    <div className="np-step-body">
                      Treat them yourself. No commitment, no minimum.
                    </div>
                  </div>
                </div>
              </div>

              <div className="step-section">
                <div className="np-eyebrow">YOU'RE COVERED WHEN</div>
                <div className="np-benefit-row">
                  <div className="np-benefit-tile">
                    <div className="np-benefit-icon" aria-hidden="true">✈</div>
                    <div className="np-benefit-title">Patient relocates</div>
                    <div className="np-benefit-body">
                      They're moving to Hawaii? They keep getting their hormones — we have providers in every state.
                    </div>
                  </div>
                  <div className="np-benefit-tile">
                    <div className="np-benefit-icon" aria-hidden="true">⚙</div>
                    <div className="np-benefit-title">Provider quits</div>
                    <div className="np-benefit-body">
                      Lost your provider? Patients stay covered without missing a refill cycle.
                    </div>
                  </div>
                  <div className="np-benefit-tile">
                    <div className="np-benefit-icon" aria-hidden="true">🗺</div>
                    <div className="np-benefit-title">Out-of-state expansion</div>
                    <div className="np-benefit-body">
                      Open in any state without hiring local providers in each one.
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {selected === true && (
          <aside className="np-grid-right">
            <div className={`np-combined-card${liteMode ? ' is-lite' : ''}`}>
              {autoApplied && hasCoupon && (
                <div
                  className="personalized-eyebrow"
                  style={{ marginBottom: 14 }}
                >
                  {personalization?.practiceName ? (
                    <>
                      Reserved for{' '}
                      <span className="personalized-eyebrow-name">
                        {personalization.practiceName}
                      </span>
                    </>
                  ) : (
                    'Your rate'
                  )}
                </div>
              )}
              <div className="np-combined-section">
                <div className="np-combined-label">One-time setup</div>
                {hasCoupon ? (
                  <>
                    <div className="np-combined-price-was">$4,997</div>
                    <div className="np-combined-price np-combined-price-now">
                      {formatDollars(finalCents)}
                    </div>
                    {!autoApplied && (
                      <div className="coupon-applied-pill">
                        ✓ {appliedPromotionCode.code} · {appliedPromotionCode.label}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="np-combined-price">$4,997</div>
                )}
                <div className="np-combined-incl">
                  Practice onboarding · Portal setup · Training · Go-live
                </div>

                {!hasCoupon && !couponOpen && (
                  <button
                    type="button"
                    className="coupon-disclosure"
                    onClick={() => setCouponOpen(true)}
                  >
                    Have a setup discount code?
                  </button>
                )}
                {!hasCoupon && couponOpen && (
                  <div className="coupon-input-row">
                    <input
                      type="text"
                      className="field-input"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase())
                        setCouponError('')
                      }}
                      placeholder="CODE"
                      style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                      disabled={couponBusy}
                    />
                    <button
                      type="button"
                      className="coupon-apply-btn"
                      onClick={applyCoupon}
                      disabled={couponBusy || !couponCode.trim()}
                    >
                      {couponBusy ? '…' : 'Apply'}
                    </button>
                  </div>
                )}
                {couponError && (
                  <div className="coupon-error">{couponError}</div>
                )}
                {hasCoupon && !autoApplied && (
                  <button
                    type="button"
                    className="coupon-remove"
                    onClick={removeCoupon}
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="np-combined-divider" />
              <div className="np-combined-section">
                <div className="np-combined-label">Ongoing</div>
                <div className="np-combined-price">
                  $55{' '}
                  <span className="np-combined-price-unit">
                    / patient / month
                  </span>
                </div>
                <div className="np-combined-incl">
                  Provider access · MD director · Compliance · Platform
                </div>
                <div className="np-combined-note">
                  Only billed while patient is active. No minimum.
                </div>
              </div>
              <div className="np-combined-divider" />

              <div className="np-combined-brands" aria-hidden="true">
                <img src="/visa.svg" alt="" className="np-combined-brand" />
                <img src="/mastercard.svg" alt="" className="np-combined-brand" />
                <img src="/amex.svg" alt="" className="np-combined-brand" />
                <img src="/discover.svg" alt="" className="np-combined-brand" />
              </div>

              {finalCents <= 0 && paymentStatus !== 'paid' ? (
                <div className="pay-card-saved" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: 'var(--accent)', fontSize: 18, lineHeight: 1 }}>✓</span>
                    <strong style={{ fontSize: 15 }}>Setup fee waived</strong>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted, var(--text-3))', lineHeight: 1.5 }}>
                    {practiceName ? practiceName : 'Your account'} has the $4,997 setup fee covered. Continue to the next step.
                  </div>
                </div>
              ) : paymentStatus === 'paid' ? (
                <div className="pay-card-saved">
                  <span className="coupon-applied-pill">
                    ✓ Setup fee paid · {formatDollars(finalCents)}
                  </span>
                </div>
              ) : achAccount?.accountId && stripeCustomerId ? (
                <>
                  <div
                    className="coupon-applied-pill"
                    style={{ marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    ✓ {achAccount.bankName} ··••{achAccount.last4}
                    <span style={{ opacity: 0.7, marginLeft: 4 }}>· from Managed Billing</span>
                  </div>
                  <button
                    type="button"
                    className="np-combined-cta"
                    onClick={payWithSavedBank}
                    disabled={payBusy}
                  >
                    {payBusy ? (
                      'Processing…'
                    ) : (
                      <>
                        Pay {formatDollars(finalCents)} with this bank
                        <span className="np-combined-cta-arrow">→</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="coupon-disclosure-quiet"
                    onClick={openPaymentPicker}
                    disabled={payBusy}
                    style={{ marginTop: 10 }}
                  >
                    use a different bank or card →
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="np-combined-cta"
                  onClick={openPaymentPicker}
                  disabled={payBusy}
                >
                  {payBusy ? (
                    'Starting payment…'
                  ) : (
                    <>
                      Pay {formatDollars(finalCents)} setup fee
                      <span className="np-combined-cta-arrow">→</span>
                    </>
                  )}
                </button>
              )}
              {payError && (
                <div className="field-error" style={{ marginTop: 10 }}>
                  {payError}
                </div>
              )}
              {payNotice && (
                <div
                  style={{
                    marginTop: 10,
                    color: 'var(--accent)',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  ✓ {payNotice}
                </div>
              )}

              <div className="np-combined-trust">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                <span>Secured by</span>
                <img
                  src="/stripe-logo.png"
                  alt="Stripe"
                  className="np-combined-stripe"
                />
                <span>· PCI DSS</span>
              </div>
            </div>
          </aside>
        )}
      </div>

      {selected === true && paymentStatus === 'paid' && (
        <div className="step-section" style={{ marginTop: 24 }}>
          <div className="info-block">
            <p className="info-block-note" style={{ margin: 0 }}>
              ✓ Network+ setup paid. You'll sign the Patient Entry Point Agreement (PEP Agreement) in the final step alongside the Provider Agreement.
            </p>
          </div>
        </div>
      )}
      </Band>
      </StepLayout>

      {methodPickerOpen && (
        <PaymentMethodPicker
          finalAmountCents={
            appliedPromotionCode?.finalAmount ?? SETUP_AMOUNT_CENTS
          }
          onConfirm={(method) => {
            setMethodPickerOpen(false)
            startPaymentWithMethod(method)
          }}
          onClose={() => setMethodPickerOpen(false)}
        />
      )}

      {(() => {
        const firstOwnerForPrefill =
          useOnboardingStore.getState().formData?.owners?.owners?.[0] || {}
        const stripeModalPrefill = {
          name: String(firstOwnerForPrefill.fullName || '').trim(),
          email: String(firstOwnerForPrefill.email || '').trim(),
        }
        return (
          <>
            {payModal && (
              <StripePaymentModal
                clientSecret={payModal.clientSecret}
                finalAmountCents={payModal.finalAmountCents}
                forcedMethod={pendingMethod}
                prefill={stripeModalPrefill}
                onSuccess={handlePaySuccess}
                onClose={() => setPayModal(null)}
              />
            )}

            {verifyModal && (
              <StripePaymentModal
                clientSecret={verifyModal.clientSecret}
                finalAmountCents={verifyModal.finalAmountCents}
                prefill={stripeModalPrefill}
                onSuccess={handleVerifySuccess}
                onClose={() => setVerifyModal(null)}
              />
            )}
          </>
        )
      })()}

      <button type="submit" className="sr-only" tabIndex={-1}>
        Continue
      </button>
    </form>
  )
}
